import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '~/store/store';
import type {
    TrackingModalData,
    DoorDashUIState
} from '~/types/doorDashUISlice.types';
import type { ApiDoorDashQuote } from '~/api/DoorDashApi/types/ApiDoorDash';

export enum BatchOperationType {
    GENERATE = 'generate',
    ACCEPT = 'accept'
}

export enum BatchOperationStatus {
    PENDING = 'pending',
    SUCCESS = 'success',
    ERROR = 'error'
}

export type {
    BatchOperationState,
    BatchResult,
    TrackingModalData,
    DoorDashUIState
} from '~/types/doorDashUISlice.types';

const initialState: DoorDashUIState = {
    quoteCacheIndicators: {},
    batchOperations: {},
    trackingModals: {},
    trackingModalData: null,
    visualIndicators: {
        mapPins: {},
        taskTypes: {},
        logos: {}
    },
    lastSyncTimestamp: 0
};

export const doorDashUISlice = createSlice({
    name: 'doorDashUI',
    initialState,
    reducers: {
        setQuoteCacheIndicator: (
            state,
            action: PayloadAction<{ taskId: string; hasCache: boolean }>
        ) => {
            const { taskId, hasCache } = action.payload;
            state.quoteCacheIndicators[taskId] = hasCache;
            state.lastSyncTimestamp = Date.now();
        },

        removeQuoteCacheIndicator: (
            state,
            action: PayloadAction<{ taskId: string }>
        ) => {
            const { taskId } = action.payload;
            delete state.quoteCacheIndicators[taskId];
            state.lastSyncTimestamp = Date.now();
        },

        clearAllQuoteCacheIndicators: (state) => {
            state.quoteCacheIndicators = {};
            state.lastSyncTimestamp = Date.now();
        },

        startBatchOperation: (
            state,
            action: PayloadAction<{
                operationId: string;
                taskIds: string[];
                operation: BatchOperationType;
            }>
        ) => {
            const { operationId, taskIds } = action.payload;
            state.batchOperations[operationId] = {
                processing: true,
                completed: 0,
                failed: 0,
                total: taskIds.length,
                results: taskIds.map((taskId) => ({
                    taskId,
                    status: BatchOperationStatus.PENDING
                }))
            };
        },

        updateBatchOperationResult: (
            state,
            action: PayloadAction<{
                operationId: string;
                taskId: string;
                status: 'success' | 'error';
                error?: string;
                quoteData?: ApiDoorDashQuote;
            }>
        ) => {
            const { operationId, taskId, status, error, quoteData } =
                action.payload;
            const operation = state.batchOperations[operationId];

            if (operation) {
                const resultIndex = operation.results.findIndex(
                    (r) => r.taskId === taskId
                );

                if (resultIndex !== -1) {
                    operation.results[resultIndex] = {
                        taskId,
                        status,
                        error,
                        quoteData
                    };

                    if (status === BatchOperationStatus.SUCCESS) {
                        operation.completed++;
                    } else {
                        operation.failed++;
                    }
                }
            }
        },

        completeBatchOperation: (
            state,
            action: PayloadAction<{ operationId: string }>
        ) => {
            const { operationId } = action.payload;
            const operation = state.batchOperations[operationId];

            if (operation) {
                operation.processing = false;
            }
        },

        removeBatchOperation: (
            state,
            action: PayloadAction<{ operationId: string }>
        ) => {
            const { operationId } = action.payload;
            delete state.batchOperations[operationId];
        },

        openTrackingModal: (
            state,
            action: PayloadAction<{ taskId: string }>
        ) => {
            const { taskId } = action.payload;
            state.trackingModals[taskId] = true;
        },

        closeTrackingModal: (
            state,
            action: PayloadAction<{ taskId: string }>
        ) => {
            const { taskId } = action.payload;
            delete state.trackingModals[taskId];
        },

        closeAllTrackingModals: (state) => {
            state.trackingModals = {};
        },

        // DoorDash tracking modal data actions
        openDoorDashTrackingModal: (
            state,
            action: PayloadAction<TrackingModalData>
        ) => {
            state.trackingModalData = action.payload;
        },

        closeDoorDashTrackingModal: (state) => {
            state.trackingModalData = null;
        },

        // Visual indicators actions
        setMapPinIndicator: (
            state,
            action: PayloadAction<{ taskId: string; isDoorDash: boolean }>
        ) => {
            const { taskId, isDoorDash } = action.payload;
            state.visualIndicators.mapPins[taskId] = isDoorDash;
        },

        setTaskTypeIndicator: (
            state,
            action: PayloadAction<{ taskId: string; isDoorDash: boolean }>
        ) => {
            const { taskId, isDoorDash } = action.payload;
            state.visualIndicators.taskTypes[taskId] = isDoorDash;
        },

        setLogoIndicator: (
            state,
            action: PayloadAction<{ taskId: string; isDoorDash: boolean }>
        ) => {
            const { taskId, isDoorDash } = action.payload;
            state.visualIndicators.logos[taskId] = isDoorDash;
        },

        removeVisualIndicators: (
            state,
            action: PayloadAction<{ taskId: string }>
        ) => {
            const { taskId } = action.payload;
            delete state.visualIndicators.mapPins[taskId];
            delete state.visualIndicators.taskTypes[taskId];
            delete state.visualIndicators.logos[taskId];
        },

        clearAllVisualIndicators: (state) => {
            state.visualIndicators = {
                mapPins: {},
                taskTypes: {},
                logos: {}
            };
        },

        // Bulk sync action for cross-component updates
        syncQuoteCacheIndicators: (
            state,
            action: PayloadAction<Record<string, boolean>>
        ) => {
            state.quoteCacheIndicators = { ...action.payload };
            state.lastSyncTimestamp = Date.now();
        }
    }
});

export const {
    setQuoteCacheIndicator,
    removeQuoteCacheIndicator,
    clearAllQuoteCacheIndicators,
    startBatchOperation,
    updateBatchOperationResult,
    completeBatchOperation,
    removeBatchOperation,
    openTrackingModal,
    closeTrackingModal,
    closeAllTrackingModals,
    openDoorDashTrackingModal,
    closeDoorDashTrackingModal,
    setMapPinIndicator,
    setTaskTypeIndicator,
    setLogoIndicator,
    removeVisualIndicators,
    clearAllVisualIndicators,
    syncQuoteCacheIndicators
} = doorDashUISlice.actions;

export default doorDashUISlice.reducer;

// Selectors
export const selectQuoteCacheIndicators = (state: RootState) =>
    state.doorDashUI.quoteCacheIndicators;

export const selectHasQuoteCache =
    (taskId: string) => (state: { doorDashUI: DoorDashUIState }) =>
        state.doorDashUI.quoteCacheIndicators[taskId] || false;

export const selectBatchOperation =
    (operationId: string) => (state: { doorDashUI: DoorDashUIState }) =>
        state.doorDashUI.batchOperations[operationId];

export const selectIsTrackingModalOpen =
    (taskId: string) => (state: { doorDashUI: DoorDashUIState }) =>
        state.doorDashUI.trackingModals[taskId] || false;

export const selectVisualIndicators = (state: {
    doorDashUI: DoorDashUIState;
}) => state.doorDashUI.visualIndicators;

export const selectLastSyncTimestamp = (state: {
    doorDashUI: DoorDashUIState;
}) => state.doorDashUI.lastSyncTimestamp;

export const selectDoorDashTrackingModalData = (state: {
    doorDashUI: DoorDashUIState;
}) => state.doorDashUI.trackingModalData;
